import React, { useState, useEffect, useRef, useContext } from 'react';
import {
    Plus, Receipt, Wallet, FileText, Trash2, Edit3, X, Camera, ArrowLeft,
    CheckCircle, History, Landmark, Image as ImageIcon, MessageSquare,
    Download, Printer, Search, ArrowRight, Phone, MapPin
} from 'lucide-react';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const VendorManagement = () => {
    const { user } = useContext(AuthContext);
    const [view, setView] = useState('list'); // 'list', 'statement', 'form'
    const [vendors, setVendors] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const printRef = useRef(); // Create a reference for the statement section
    // Statement Specific State
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [vendorStatement, setVendorStatement] = useState({ invoices: [], payments: [] });

    useEffect(() => { loadInitialData(); }, []);

    const loadInitialData = async () => {
        try {
            const [vRes, iRes] = await Promise.all([
                api.get('get_vendors.php'),
                api.get('get_all_vendor_invoices.php')
            ]);
            setVendors(vRes.data.vendors || []);
            setInvoices(iRes.data.data || []);
        } catch (err) { console.error("Error loading vendor data", err); }
    };

    const fetchVendorStatement = async (vendor) => {
        setSelectedVendor(vendor);
        const res = await api.get(`get_vendor_statement.php?vendor_id=${vendor.id}`);
        setVendorStatement(res.data);
        setView('statement');
    };

    // Filter Logic
    const filteredVendors = vendors.filter(v =>
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.contact_person && v.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calculate Statement Totals

    // 1. Function to trigger Browser Print (Styled for Ledger)
    const handlePrint = () => {
        window.print();
    };

    // 2. Function to Export Ledger to CSV (Excel compatible)
    const handleExportCSV = () => {
        const headers = ["Date,Particulars,Debit (Purchases),Credit (Payments),Balance\n"];

        const rows = [...vendorStatement.invoices, ...vendorStatement.payments]
            .sort((a, b) => new Date(a.invoice_date || a.payment_date) - new Date(b.invoice_date || b.payment_date))
            .map(item => {
                const isInv = !!item.invoice_no;
                const date = item.invoice_date || item.payment_date;
                const desc = isInv ? `Purchase: #${item.invoice_no}` : `Payment: ${item.notes || 'Settlement'}`;
                const debit = isInv ? item.total_amount : 0;
                const credit = !isInv ? item.amount_paid : 0;
                return `${date},${desc},${debit},${credit},0`; // Simplified balance
            });

        const csvContent = "data:text/csv;charset=utf-8," + headers + rows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${selectedVendor.name}_Statement.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // Place this inside your VendorManagement component, before the return
    const invoicesList = vendorStatement.invoices || [];
    const paymentsList = vendorStatement.payments || [];

    const totalBilled = invoicesList.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
    const totalPaid = paymentsList.reduce((sum, pay) => sum + parseFloat(pay.amount_paid || 0), 0);
    const outstanding = totalBilled - totalPaid;

    // Breakdown payments by mode
    const paymentModes = paymentsList.reduce((acc, pay) => {
        const mode = pay.payment_mode || 'Other';
        acc[mode] = (acc[mode] || 0) + parseFloat(pay.amount_paid || 0);
        return acc;
    }, {});
    return (
        <div className="max-w-7xl mx-auto space-y-8 p-4">
            {/* HEADER & SEARCH */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                <div className="flex-1">
                    <h1 className="text-5xl font-black text-slate-900 tracking-tighter italic">Vendor Directory</h1>
                    <div className="mt-6 relative max-w-md group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={20} />
                        <input
                            type="text"
                            placeholder="Search supplier name or contact..."
                            className="w-full pl-12 pr-6 py-4 bg-white border border-slate-100 rounded-2xl font-bold shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                {view === 'list' && (
                    <button onClick={() => setView('form')} className="bg-slate-900 text-white px-10 py-5 rounded-[24px] font-black shadow-2xl flex items-center gap-3 hover:bg-indigo-600 transition-all">
                        <Plus size={20} /> Add Supplier
                    </button>
                )}
            </div>

            {view === 'list' ? (
                /* MASONRY STYLE VENDOR GRID */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVendors.map(vendor => {
                        const vendorInvoices = invoices.filter(i => i.vendor_id === vendor.id);
                        const vendorPending = vendorInvoices.reduce((a, b) => a + (b.total_amount - b.paid_amount), 0);

                        return (
                            <div
                                key={vendor.id}
                                onClick={() => fetchVendorStatement(vendor)}
                                className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm hover:shadow-2xl hover:border-indigo-100 cursor-pointer transition-all group flex flex-col justify-between h-full"
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="p-4 bg-slate-50 rounded-2xl group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                            <Landmark size={24} />
                                        </div>
                                        <div className="px-3 py-1 bg-slate-50 rounded-full text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:bg-indigo-50 group-hover:text-indigo-600">
                                            {vendorInvoices.length} Bills
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900 uppercase italic leading-tight group-hover:text-indigo-600 transition-colors">{vendor.name}</h3>
                                    <div className="mt-4 space-y-2">
                                        <p className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-tighter">
                                            <Phone size={12} /> {vendor.phone || 'No Phone'}
                                        </p>
                                        <p className="flex items-center gap-2 text-[10px] font-black text-slate-300 uppercase">
                                            <MapPin size={12} /> {vendor.address ? (vendor.address.substring(0, 30) + '...') : 'No Address'}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-slate-50 flex justify-between items-end">
                                    <div>
                                        <p className="text-[9px] font-black text-slate-300 uppercase">Status</p>
                                        <p className={`text-[10px] font-black uppercase ${vendorPending > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                            {vendorPending > 0 ? 'Payment Due' : 'Settled'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] font-black text-slate-300 uppercase">Outstanding</p>
                                        <p className="font-black text-slate-900 italic text-xl group-hover:text-rose-500 transition-colors">
                                            ₹{vendorPending.toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : view === 'statement' ? <div className="space-y-8 animate-in fade-in" id="printable-area" ref={printRef}>

                {/* SECTION 1: PRINT-ONLY OFFICIAL HEADER (Hidden on Screen) */}
                <div className="hidden print:flex justify-between items-start border-b-2 border-slate-900 pb-8 mb-8">
                    <div>
                        <h1 className="text-4xl font-black italic tracking-tighter text-slate-900">{user.company_name}</h1>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-1">Interior Designers & Furniture Manufacturers</p>
                        <div className="mt-4 text-[11px] font-medium text-slate-600 leading-relaxed">
                            <p>Shop No. 04, Your Address Line 1,</p>
                            <p>Near Landmark, Virar (W), Palghar - 401303</p>
                            <p>{`Contact: +91 98XXX XXXXX | Email: info@abc.com`}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="bg-slate-900 text-white px-6 py-2 rounded-lg font-black text-sm uppercase mb-4">
                            Vendor Statement
                        </div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">GSTIN: 27AAAAA0000A1Z5</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Date: {new Date().toLocaleDateString('en-IN')}</p>
                    </div>
                </div>

                {/* SECTION 2: SCREEN ACTIONS (Hidden on Print) */}
                <div className="flex justify-between items-center print:hidden">
                    <button onClick={() => setView('list')} className="flex items-center gap-2 p-4 bg-indigo-50 rounded-2xl text-indigo-600 font-black text-[10px] uppercase">
                        <ArrowLeft size={16} /> Back to List
                    </button>
                    <div className="flex gap-2">
                        <button onClick={handlePrint} className="p-4 bg-white border border-slate-100 rounded-2xl text-slate-600 hover:bg-slate-50"><Printer size={20} /></button>
                        <button onClick={handleExportCSV} className="p-4 bg-indigo-600 rounded-2xl text-white shadow-xl shadow-indigo-100"><Download size={20} /></button>
                    </div>
                </div>

                {/* SECTION 3: STATEMENT SUMMARY (FORCED COLORS FOR PRINT) */}
                <div className="print-force-bg bg-slate-900 rounded-[48px] p-12 text-white shadow-2xl overflow-hidden print:rounded-3xl print:p-8">
                    <div className="flex flex-col md:flex-row justify-between gap-8">
                        <div>
                            <p className="text-indigo-400 font-black text-[10px] uppercase tracking-[0.3em] mb-2 print-text-white">Statement For</p>
                            <h2 className="text-5xl font-black italic tracking-tighter print-text-white">{selectedVendor.name}</h2>
                            <p className="text-xs font-bold opacity-60 mt-2 print-text-white">GSTIN: {selectedVendor.gstin || 'N/A'}</p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-md rounded-[32px] p-8 border border-white/10 text-right print:border-white/20">
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1 print-text-white">Total Outstanding</p>
                            {/* Explicitly styled for print visibility */}
                            <h2 className="text-5xl font-black italic print-text-white">
                                ₹{outstanding.toLocaleString('en-IN')}
                            </h2>
                        </div>
                    </div>
                </div>

                {/* SECTION 4: DETAILED LEDGER TABLE */}
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden print:border-slate-200 print:rounded-2xl">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest print:bg-slate-100">
                            <tr>
                                <th className="p-8 print:p-4">Date</th>
                                <th className="p-8 print:p-4">Particulars</th>
                                <th className="p-8 print:p-4 text-right">Debit (₹)</th>
                                <th className="p-8 print:p-4 text-right">Credit (₹)</th>
                                <th className="p-8 print:p-4 text-right">Doc</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 print:divide-slate-200">
                            {[...vendorStatement.invoices, ...vendorStatement.payments]
                                .sort((a, b) => new Date(a.invoice_date || a.payment_date) - new Date(b.invoice_date || b.payment_date))
                                .map((item, idx) => {
                                    const isInvoice = !!item.invoice_no;
                                    return (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-8 print:p-4 text-xs font-bold text-slate-500">{item.invoice_date || item.payment_date}</td>
                                            <td className="p-8 print:p-4">
                                                <p className="font-black text-slate-900 text-sm uppercase italic">
                                                    {isInvoice ? `Purchase: Inv #${item.invoice_no}` : `Payment: ${item.notes || 'Settlement'}`}
                                                </p>
                                                <p className="text-[9px] font-black text-indigo-500 uppercase">{isInvoice ? (item.project_name || 'General Inventory') : 'Bank/Cash Out'}</p>
                                            </td>
                                            <td className="p-8 print:p-4 text-right font-black text-rose-500">{isInvoice ? `₹${parseFloat(item.total_amount).toLocaleString()}` : '-'}</td>
                                            <td className="p-8 print:p-4 text-right font-black text-emerald-600">{!isInvoice ? `₹${parseFloat(item.amount_paid).toLocaleString()}` : '-'}</td>
                                            <td className="p-8 print:p-4 text-right print:hidden">
                                                {(item.attachment_path || item.inventory_path) && <ImageIcon size={16} className="inline text-slate-300" />}
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                {/* PRINT-ONLY FOOTER */}
                <div className="hidden print:block mt-20 text-center border-t border-slate-100 pt-8">
                    <p className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-300">{`${user.company_name} • Authorized Signatory`}</p>
                    <div className="mt-12 h-px w-48 bg-slate-200 mx-auto"></div>
                </div>
            </div>
                : null}
        </div>
    );
};

export default VendorManagement;